import { ref } from "vue"
import type { KirbyImageData } from "@/queries/image"

const isZoomed = ref(false)
const zoomData = ref<KirbyImageData | null>(null)
const zoomSourceRect = ref<DOMRect | null>(null)
const zoomSourceElement = ref<HTMLImageElement | null>(null)
const zoomScrollY = ref(0)

export const computeZoomRect = (ratio: number) => {
  const pad =
    0.75 * parseFloat(getComputedStyle(document.documentElement).fontSize) // = p-3
  const vpW = window.innerWidth
  const vpH = window.innerHeight
  const availW = vpW - 2 * pad
  const availH = vpH - 2 * pad
  const availRatio = availW / availH
  const width = ratio > availRatio ? availW : availH * ratio
  const height = ratio > availRatio ? availW / ratio : availH
  return { width, height }
}

// Toggle visibility of *every* on-page copy of the zoomed image, not just the
// clicked node: the gallery Swiper (loop + observe-slide-children) relocates and
// duplicates slide DOM, so by the time the reverse flight runs the landing spot
// may be occupied by a different/duplicate `<img>` than the one we captured.
// Each zoomable image is tagged `data-zoom-id="<uuid>"` (Lightbox_Figure, Block
// Image); Swiper copies that attribute onto its duplicates, so this catches them.
const setZoomSourceVisibility = (visible: boolean) => {
  const value = visible ? "" : "hidden"
  const uuid = zoomData.value?.uuid
  if (uuid) {
    document
      .querySelectorAll<HTMLElement>(`[data-zoom-id="${uuid}"]`)
      .forEach(el => (el.style.visibility = value))
  }
  // Fallback for the captured node in case it isn't tagged.
  if (zoomSourceElement.value) zoomSourceElement.value.style.visibility = value
}

// Hide the real image(s) while the flight clone is on screen. Safe to call on
// both the "in" and "out" flights — on "out" it re-asserts the hide against any
// node Swiper relocated/duplicated since the "in" flight.
export const hideZoomSource = () => setZoomSourceVisibility(false)

// Restore a single element instantly, bypassing any CSS opacity/visibility
// transitions on the element so there's no visible fade-in flash.
const restoreEl = (el: HTMLElement) => {
  el.style.transition = "none"
  el.style.visibility = ""
  el.style.opacity = ""
  void el.getBoundingClientRect() // force reflow to commit before re-enabling transitions
  el.style.transition = ""
}

// Standalone (module-level state) so the page-transition FSM can call it
// directly when a flight finishes, without instantiating the composable.
// On the flight path this is called from ZoomFlight's onBeforeUnmount so
// the clone is already gone and zoomData/zoomSourceElement are still populated.
export const finalizeZoomClose = () => {
  const uuid = zoomData.value?.uuid
  if (uuid) {
    document
      .querySelectorAll<HTMLElement>(`[data-zoom-id="${uuid}"]`)
      .forEach(restoreEl)
  }
  // Fallback for the captured node in case it isn't tagged.
  if (zoomSourceElement.value) restoreEl(zoomSourceElement.value)
  zoomData.value = null
  zoomSourceRect.value = null
  zoomSourceElement.value = null
  zoomScrollY.value = 0
}

export const useZoom = () => {
  const openZoom = (
    data: KirbyImageData,
    sourceRect?: DOMRect,
    element?: HTMLImageElement,
  ) => {
    zoomData.value = data
    zoomSourceRect.value = sourceRect ?? null
    zoomSourceElement.value = element ?? null
    zoomScrollY.value = window.scrollY
    isZoomed.value = true
  }

  // Only flips the state flag — the reverse flight still needs the source
  // rect/element/data, so the actual teardown is deferred to finalizeZoomClose.
  const closeZoom = () => {
    isZoomed.value = false
  }

  return {
    isZoomed,
    zoomData,
    zoomSourceRect,
    zoomSourceElement,
    zoomScrollY,
    openZoom,
    closeZoom,
    finalizeZoomClose,
  }
}
